import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { C } from 'shared/helpers';
import { PEMmap } from 'shared/constants';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useOnClickOutside, useDatabaseSync } from 'shared/hooks';
import { withRouter } from 'react-router-dom';

import Styles from './Sidebar.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserCircle,
  faPlus,
  faFilter,
  faTimes,
  faPowerOff,
  faEnvelope,
  faQuestion,
  faCogs,
  faUsers,
  faBan,
  faClipboard,
} from '@fortawesome/free-solid-svg-icons';

import { Creators as PEMS, updateCreatedPEM } from 'store/ducks/pems';
import { Creators as Culturas } from 'store/ducks/culturas';
import { Creators as Cidades } from 'store/ducks/cidades';
import { Creators as Estados } from 'store/ducks/estados';
import { Creators as User } from 'store/ducks/user';

import Section from './Section/Section';
import { Select, SideInput, Warning } from 'components/Template';
import WithPermission from 'components/Template/WithPermission/WithPermission';

function filterPems(pems: PemAPI[], filter: { [key: string]: string }) {
  const pemAndMatches = (pems || []).map((pem) => {
    const matches = Object.entries(PEMmap).map(
      ([filterKey, pemKey]: [string, string]) =>
        !filter[filterKey] || pem[pemKey] === filter[filterKey],
    );
    return [
      pem,
      matches.reduce((r, b) => b && r, true),
      ...matches.map((_, i, arr) => {
        return arr.reduce((r, b, i2) => (i !== i2 ? b : true) && r, true);
      }),
    ] as const;
  });
  return {
    allFilters: pemAndMatches
      .filter(([_pem, matchesAll]) => matchesAll)
      .map(([pem]) => pem),
    ...Object.keys(PEMmap).reduce(
      (r, filterKey, i) => ({
        ...r,
        [filterKey]: pemAndMatches
          .filter(
            ([_pem, _matchesAll, ...filterKeyMatches]) => filterKeyMatches[i],
          )
          .map(([pem]) => pem),
      }),
      {},
    ),
  } as {
    allFilters: PemAPI[],
  } & {
    [key in keyof typeof PEMmap]: PemAPI[]
  };
}

const Sidebar = ({ open, toggle, history }) => {
  const ref = useRef(null);
  const dispatch = useDispatch();
  const { Sidebar, Open, Header, Container, Center } = Styles;
  const [CPSection, toggleCPS] = useState(false);
  const [FiltersSection, toggleFS] = useState(false);
  const [SettingsSection, toggleSS] = useState(false);
  const [logoutWarning, setLogoutWarning] = useState(false);
  const [filterOpenState, setFilterOpenState] = useState(false);

  useOnClickOutside(ref, () => toggle(false));

  const usr = useSelector((state) => state.user.data);
  const awaitingSync = useSelector(
    (state) => state.queue.data && state.queue.data.length,
  );
  const filterCidades = useSelector((state) => state.cidades.data);
  const pemCidades = useSelector((state) => state.cidades.data);
  const culturas = useSelector((state) => state.culturas.data);
  const estados = useSelector((state) => state.estados.data);
  const reduxPems = useSelector((state) => state.pems);
  const { created, data } = reduxPems;
  useEffect(() => {
    if (created && data.find((p) => p.id === created.id)) {
      history.push(`/pem/${created.id}/data`);
      dispatch(updateCreatedPEM(null));
    }
  }, [created, data, history, dispatch]);
  const roles = useSelector((state) => state.user.data.roles);
  const requirePem = useRef(false);

  useDatabaseSync('pems', () => PEMS.getPems());
  useDatabaseSync('estados', () => Estados.getEstados());
  useDatabaseSync('culturas', () => Culturas.getCulturas());

  useEffect(() => {
    if (reduxPems.error) {
      if (['invalid data', 'duplicate entry'].includes(reduxPems.error)) {
        toggle(true);
      }
    }
  }, [reduxPems.error, toggle]);

  const [PEM, setPEM] = useState<{
    estado: string;
    municipio: string;
    cultura: string;
    produtor: string;
    data: string;
    ETEC: string;
  }>({
    estado: '',
    municipio: '',
    cultura: '',
    produtor: '',
    data: moment().format('YYYY-MM-DD'),
    ETEC: '',
  });

  const [Filter, setFilter] = useState({
    ETEC: '',
    cultura: '',
    data: '',
    estado: '',
    municipio: '',
    PEM: '',
  });
  useEffect(() => {
    if (!Filter.estado) {
      setFilter(old => ({...old, municipio: ''}))
    }
  }, [Filter.estado]);

  useEffect(() => {
    setPEM((x) => (usr.id ? { ...x, ETEC: usr.id } : x));
    setFilter((x) => (usr.id ? { ...x, ETEC: usr.id } : x));
  }, [usr]);

  useEffect(() => {
    dispatch(Culturas.getCulturas());
    dispatch(Estados.getEstados());
    dispatch(
      Cidades.getCidades(
        (cidade) =>
          cidade.estado_id === Filter.estado || cidade.estado_id === PEM.estado,
      ),
    );
  }, [dispatch, Filter.ETEC, Filter.estado, PEM.estado, PEM.ETEC]);

  const createPEM = () => {
    dispatch(PEMS.addPem({ ...PEM }));
    requirePem.current = true;
    toggle(false);
  };

  const {
    allFilters: FilteredPems,
    etec: filterNotByEtec,
    cultura: filterNotByCultura,
    data: filterNotByData,
    estado: filterNotByEstado,
    municipio: filterNotByMunicipio,
  } = useMemo(() => filterPems(data as PemAPI[], Filter), [Filter, data]);

  const Functions = {
    PEM: {
      plantations: useCallback(
        () =>
          culturas.map((cultura) => ({
            label: cultura.nome,
            value: cultura.id,
          })),
        [culturas],
      ),
      states: useCallback(
        () =>
          estados.map((estado) => ({
            label: estado.nome,
            value: estado.id,
          })),
        [estados],
      ),
      cities: useCallback(
        () =>
          pemCidades
            .filter((cidade) => Number(cidade.estado.id) === Number(PEM.estado))
            .map((cidade) => ({
              label: cidade.nome,
              value: cidade.id,
            })),
        [PEM.estado, pemCidades],
      ),
    },
    Filters: {
      plantations: useMemo(
        () =>
          culturas
            .filter((c) => filterNotByCultura.find((p) => Number(p.cultura_id) === Number(c.id)))
            .map((cultura) => ({
              label: cultura.nome,
              value: cultura.id,
            })),
        [culturas, FilteredPems],
      ),
      states: useMemo(
        () =>
          estados
            .filter((c) => filterNotByEstado.find((p) => Number(p.estado_id) === Number(c.id)))
            .map((estado) => ({
              label: estado.nome,
              value: estado.id,
            })),
        [estados, FilteredPems],
      ),
      cities: useMemo(
        () =>
          filterCidades
            .filter(
              (c) =>
                Number(c.estado.id) === Number(Filter.estado) &&
                filterNotByMunicipio.find((p) => p.cidade_id === c.id),
            )
            .map((cidade) => ({
              label: cidade.nome,
              value: cidade.id,
            })),
        [filterCidades, Filter.estado, FilteredPems],
      ),
      dates: useMemo(
        () =>
        filterNotByData.reduce(
            (r, x) =>
              !r.find((d) => d.value === x.data)
                ? [
                    ...r,
                    {
                      label: moment(x.data).format('DD/MM/YYYY'),
                      value: x.data,
                    },
                  ]
                : r,
            [],
          ),
        [FilteredPems],
      ),
      PEM: useMemo(
        () =>
          FilteredPems.map((pem) => ({
            value: pem.id,
            label: pem.nome,
          })),
        [FilteredPems],
      ),
    },
  };

  useEffect(() => {
    dispatch(PEMS.getPems(() => true));
    dispatch(Estados.getEstados());
    dispatch(Culturas.getCulturas());
  }, [open, dispatch]);

  return (
    <div ref={ref} className={C(Sidebar, [open || logoutWarning, Open])}>
      {open && (
        <div>
          <Warning
            message='Você efetuou modificações que não foram sincronizadas. Elas serão perdidas se você sair sem sincronizar antes.'
            abort={true}
            act={() => dispatch(User.logout())}
            open={logoutWarning}
            setOpen={setLogoutWarning}
          />
          <Warning
            message='Esta combinação de cultura, ETEC, produtor e data já está cadastrada.'
            abort={false}
            act={() => dispatch(PEMS.clearPemError())}
            open={reduxPems.error === 'duplicate entry'}
          />
          <Warning
            message='Dados inválidos.'
            abort={false}
            act={() => dispatch(PEMS.clearPemError())}
            open={reduxPems.error === 'invalid data'}
          />
          <div className={C(Header)}>
            <h2>
              <FontAwesomeIcon
                style={{ fontSize: '1.5em' }}
                icon={faUserCircle}
              />
              <span>{usr.name}</span>
              <div id='sidebar_close_btn' onClick={() => toggle(false)}>
                <FontAwesomeIcon style={{ cursor: 'pointer' }} icon={faTimes} />
              </div>
            </h2>
          </div>
          <hr />
          <WithPermission permission='pems.create'>
            <Section
              icon={faPlus}
              title='NOVA PEM'
              open={CPSection}
              toggle={() => {
                toggleFS(false);
                toggleCPS(!CPSection);
                toggleSS(false);
              }}
              id='create_pem_btn'
            >
              <div className={Container}>
                <Select
                  id='etec_selector'
                  selectKey={'PEM'}
                  label={'ETEC'}
                  placeholder={'ETEC'}
                  valueRef={PEM.ETEC}
                  setValueRef={setPEM}
                  options={
                    usr
                      ? [{ label: usr.name, value: usr.id }]
                      : []
                  }
                  stateKey={'ETEC'}
                  disabled={(proprieties) => proprieties.options.length <= 1} // If there is only one etec (The user is not of type master) disable filter by this
                />
              </div>
              <div className={Container}>
                <Select
                  selectKey={'PEM'}
                  label={'Cultura'}
                  placeholder={'Selecione...'}
                  valueRef={PEM.cultura}
                  setValueRef={setPEM}
                  options={Functions.PEM.plantations()}
                  stateKey={'cultura'}
                  id='cultura_selector'
                />
              </div>
              <div className={Container}>
                <SideInput
                  label={'Produtor'}
                  valueRef={PEM.produtor}
                  setValueRef={setPEM}
                  stateKey={'produtor'}
                  íd='produtor_input'
                />
              </div>
              <div className={Container}>
                <Select
                  id='estado_selector'
                  selectKey={'PEM'}
                  label={'Estado'}
                  placeholder={'Selecione...'}
                  valueRef={PEM.estado}
                  setValueRef={setPEM}
                  options={Functions.PEM.states()}
                  stateKey={'estado'}
                  onChoice={(value) => {
                    setPEM((pem) => ({ ...pem, estado: value, municipio: '' }));
                  }}
                />
              </div>
              <div className={Container}>
                <Select
                  id='municipio_selector'
                  selectKey={'PEM'}
                  label={'Município'}
                  placeholder={
                    PEM.estado ? 'Selecione...' : 'Selecione um estado'
                  }
                  disabled={!PEM.estado}
                  valueRef={PEM.municipio}
                  setValueRef={setPEM}
                  options={Functions.PEM.cities()}
                  stateKey={'municipio'}
                />
              </div>
              <div className={Container}>
                <SideInput
                  type={'date'}
                  label={'Data'}
                  placeholder={'dd/mm/aaaa'}
                  mask={'00/00/0000'}
                  valueRef={PEM.data}
                  setValueRef={setPEM}
                  stateKey={'data'}
                  required={true}
                />
              </div>
              <div className={Container}>
                <button
                  style={{
                    cursor: 'pointer',
                    lineHeight: '1.2',
                    width: 'auto',
                    fontSize: '1em',
                  }}
                  onClick={createPEM}
                >
                  CRIAR NOVA PEM
                </button>
              </div>
            </Section>
          </WithPermission>
          <Section
            icon={faFilter}
            title='FILTROS'
            open={FiltersSection}
            toggle={() => {
              toggleFS(!FiltersSection);
              toggleCPS(false);
              toggleSS(false);
            }}
          >
            <div className={Container}>
              <Select
                id='etec_filter_selector'
                selectKey={'Filter'}
                label={'ETEC'}
                placeholder={'Selecione...'}
                valueRef={Filter.ETEC}
                setValueRef={setFilter}
                stateKey={'ETEC'}
                options={
                  usr ? [{ label: usr.name, value: usr.id }] : []
                }
                disabled={(proprieties) => proprieties.options.length <= 1}
              />
            </div>
            <div className={Container}>
              <Select
                id='cultura_filter_selector'
                selectKey={'Filter'}
                label={'Cultura'}
                placeholder={'Selecione...'}
                valueRef={Filter.cultura}
                setValueRef={setFilter}
                stateKey={'cultura'}
                options={Functions.Filters.plantations}
                nullable
              />
            </div>{' '}
            <div className={Container}>
              <Select
                id='data_filter_selector'
                selectKey={'Filter'}
                label={'Data'}
                placeholder={'Selecione...'}
                valueRef={Filter.data}
                setValueRef={setFilter}
                options={Functions.Filters.dates}
                stateKey={'data'}
                nullable
              />
            </div>{' '}
            <div className={Container}>
              <Select
                id='estado_filter_selector'
                selectKey={'Filter'}
                label={'Estado'}
                placeholder={'Selecione...'}
                valueRef={Filter.estado}
                setValueRef={setFilter}
                stateKey={'estado'}
                options={Functions.Filters.states}
                nullable
              />
            </div>{' '}
            <div className={Container}>
              <Select
                id='municipio_filter_selector'
                selectKey={'Filter'}
                label={'Município'}
                placeholder={
                  Filter.estado ? 'Selecione...' : 'Selecione um estado'
                }
                valueRef={Filter.municipio}
                setValueRef={setFilter}
                disabled={!Filter.estado}
                stateKey={'municipio'}
                options={Functions.Filters.cities}
                nullable
              />
            </div>
            <div className={Container}>
              <Select
                id='pem_filter_selector'
                selectKey={'Filter'}
                label={'PEM'}
                placeholder={'Selecione...'}
                valueRef={Filter.PEM}
                setValueRef={setFilter}
                stateKey={'PEM'}
                openRef={filterOpenState}
                setOpenRef={setFilterOpenState}
                options={Functions.Filters.PEM}
                onChoice={
                  open &&
                  ((id) => history.push(`/pem/${id}/data`) || toggle(false))
                }
              />
            </div>
          </Section>
          {roles.some((role) =>
            role.name.toLowerCase().includes('master 1'),
          ) ? (
            <Section
              icon={faCogs}
              title='CONFIGURAÇÕES'
              open={SettingsSection}
              toggle={() => {
                toggleSS(!SettingsSection);
                toggleFS(false);
                toggleCPS(false);
              }}
            >
              <div className={Center}>
                <button onClick={() => history.push('/products')}>
                  <FontAwesomeIcon icon={faClipboard} />
                  <span>Produtos</span>
                </button>
                <WithPermission permission={'users.read'}>
                  <button onClick={() => history.push('/users')}>
                    <FontAwesomeIcon icon={faUsers} />
                    <span>Usuários</span>
                  </button>
                </WithPermission>
                <WithPermission permission='roles.read'>
                  <button onClick={() => history.push('/groups')}>
                    <FontAwesomeIcon icon={faBan} />
                    <span>Grupos e Permissões</span>
                  </button>
                </WithPermission>
              </div>
            </Section>
          ) : null}
          <div className={Styles.Footer}>
            <hr />
            <div>
              <button>
                <FontAwesomeIcon icon={faQuestion} />
                <span>Ajuda</span>
              </button>
              <button
                onClick={() => {
                  history.push('/contact');
                  toggle(false);
                }}
              >
                <FontAwesomeIcon icon={faEnvelope} />
                <span>Contato</span>
              </button>
              <button
                onClick={
                  awaitingSync
                    ? () => setLogoutWarning(true)
                    : () => {
                        history.push('/');
                        dispatch(User.logout());
                      }
                }
              >
                <FontAwesomeIcon icon={faPowerOff} />
                <span>Sair</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

Sidebar.defaultProps = {
  history: undefined,
};

Sidebar.propTypes = {
  open: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  history: PropTypes.any,
};

export default withRouter(Sidebar);
